import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAdminTournaments, useAdminMutation } from "../../lib/adminData";
import { useIsAdmin, useSession } from "../../lib/me";
import type { Tournament } from "../../lib/types";
import { formatDateRange } from "../../lib/format";
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  ChevronRightIcon,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionCard,
  Skeleton,
  stagger,
  TournamentStatusBadge,
  Workbench,
} from "../../components/ui";

export function AdminTournaments() {
  const qc = useQueryClient();
  const list = useAdminTournaments();

  // Everything on this page except the list itself belongs to a global admin:
  // the venue library, branding, the About page, the allowlist, the users page,
  // and creating an event are all refused to a tournament-scoped role. The list
  // arrives already filtered to the tournaments a scoped caller holds a grant
  // on, so what is left is safe to show them.
  const isAdmin = useIsAdmin();
  const session = useSession();
  const directs = session.data?.tournamentRoles?.some((r) => r.role === "tournamentAdmin");

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const create = useAdminMutation("_", () =>
    api.post<Tournament>("/admin/tournaments", { name, startDate, endDate }),
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name && startDate && endDate)
      create.mutate(undefined, {
        onSuccess: () => {
          setName("");
          setStartDate("");
          setEndDate("");
          void qc.invalidateQueries({ queryKey: ["admin-tournaments"] });
        },
      });
  };

  const all = list.data?.tournaments ?? [];
  const archivedCount = all.filter((t) => t.status === "archived").length;
  // Presentation only — /admin/tournaments still returns everything, so an
  // admin can always get an archived event back.
  const tournaments = showArchived ? all : all.filter((t) => t.status !== "archived");

  const tournamentList = (
    <div className="animate-rise" style={stagger(1)}>
      {list.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ) : tournaments.length === 0 ? (
        <EmptyState
          title={all.length === 0 ? "No tournaments yet" : "No active tournaments"}
          hint={
            all.length > 0
              ? "Every tournament is archived. Tick “Show archived” to see them."
              : isAdmin
                ? "Start your first one under “New tournament”."
                : directs
                  ? "Create a tournament whenever you are ready to run one."
                  : // A scoped role cannot create one, so the way out of this
                    // state is someone else granting them a role on an event.
                    "An event shows here once an organizer gives you a role on it."
          }
        />
      ) : (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          {tournaments.map((t) => (
            <Link
              key={t.id}
              to={`/admin/t/${t.id}`}
              className="group flex items-center justify-between gap-3 p-4 transition-colors hover:bg-brand-50/40"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-navy-950 transition-colors group-hover:text-brandText">
                  {t.name}
                </p>
                <p className="text-xs text-slate-500">{formatDateRange(t.startDate, t.endDate)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {/* "Is anything waiting on me?" answered before anything is
                    opened (console spec §6.2). Present only on a list built
                    for a caller who can act on the queue. */}
                {t.pending ? (
                  <Badge tone="amber" pulse>
                    {t.pending} pending
                  </Badge>
                ) : null}
                <TournamentStatusBadge status={t.status} />
                <ChevronRightIcon className="h-4 w-4 text-slate-300 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600" />
              </div>
            </Link>
          ))}
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="Tournaments"
        subtitle={
          isAdmin
            ? "Create events and manage everything from divisions to brackets."
            : "The events you have been given a role on."
        }
        actions={
          <>
            {/* Archived is a view of the same list, not a global action, so it
                stays for a scoped caller — whose one tournament may be it. */}
            {archivedCount > 0 ? (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                Show archived ({archivedCount})
              </label>
            ) : null}
            {isAdmin ? (
              <>
                <ButtonLink variant="secondary" to="/admin/venues">
                  Venues
                </ButtonLink>
                <ButtonLink variant="secondary" to="/admin/branding">
                  Branding
                </ButtonLink>
                <ButtonLink variant="secondary" to="/admin/about">
                  About
                </ButtonLink>
                <ButtonLink variant="secondary" to="/admin/users">
                  Users
                </ButtonLink>
                <ButtonLink variant="secondary" to="/admin/admins">
                  Admins
                </ButtonLink>
              </>
            ) : directs ? (
              <ButtonLink to="/host/new">Create a tournament</ButtonLink>
            ) : null}
          </>
        }
      />

      {/*
        Bare rather than a Workbench with an empty rail: the layout always
        reserves its 20rem aside, so a caller with nothing to put there would
        read the list across two thirds of the screen beside a blank column.
      */}
      {isAdmin ? (
        <Workbench
          aside={
            <SectionCard title="New tournament" className="animate-rise" style={stagger(2)}>
              <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" className="sm:col-span-2">
                  <Input
                    placeholder="e.g. Summer Slam 2026"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Field>
                <Field label="Start date">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </Field>
                <Field label="End date">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? "Creating…" : "Create tournament"}
                  </Button>
                </div>
              </form>
            </SectionCard>
          }
        >
          {tournamentList}
        </Workbench>
      ) : (
        tournamentList
      )}
    </div>
  );
}
